import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import ImageUpload from '../../components/ImageUpload';
import { Plus, Edit2, Trash2, X, Save, Image, MoveUp, MoveDown, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Preview Carousel Component
const PreviewCarousel = ({ slides, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  if (slides.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors"
        >
          <X className="w-8 h-8" />
        </button>
        
        {/* Preview Label */}
        <div className="absolute -top-12 left-0 text-white font-semibold flex items-center gap-2">
          <Eye className="w-5 h-5" />
          <span>Preview Mode</span>
        </div>

        {/* Carousel Container */}
        <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl group bg-gray-900">
          {/* Images */}
          <div className="relative w-full h-full">
            {slides.map((slide, index) => (
              <div
                key={slide.id || index}
                className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                  index === currentIndex
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-105'
                }`}
              >
                <img
                  src={slide.image}
                  alt={slide.title || `Slide ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {/* Overlay for text */}
                {(slide.title || slide.subtitle) && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                      {slide.title && (
                        <h3 className="text-3xl font-bold mb-3">{slide.title}</h3>
                      )}
                      {slide.subtitle && (
                        <p className="text-lg text-gray-200 mb-4 line-clamp-2">{slide.subtitle}</p>
                      )}
                      {slide.button_text && slide.button_link && (
                        <span className="inline-block px-6 py-3 bg-[#F5B700] text-gray-900 font-semibold rounded-lg text-sm">
                          {slide.button_text}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {slides.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
              >
                <ChevronLeft className="w-6 h-6 text-gray-800" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
              >
                <ChevronRight className="w-6 h-6 text-gray-800" />
              </button>
            </>
          )}

          {/* Dots Navigation */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-8 h-2 bg-white'
                    : 'w-2 h-2 bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Slide Counter */}
        <div className="text-center text-white mt-4">
          Slide {currentIndex + 1} of {slides.length}
        </div>
      </div>
    </div>
  );
};

const AdminHero = () => {
  const { getAuthHeader } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: '',
    button_text: 'Learn More',
    button_link: '/about'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hero-images`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching hero images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image) {
      toast({ title: 'Error', description: 'Please upload an image', variant: 'destructive' });
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/hero-images/${editingId}`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Hero slide updated successfully' });
      } else {
        if (items.length >= 5) {
          toast({ title: 'Error', description: 'Maximum 5 hero slides allowed', variant: 'destructive' });
          return;
        }
        await axios.post(`${API_URL}/api/hero-images`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Hero slide created successfully' });
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save hero slide', variant: 'destructive' });
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      subtitle: item.subtitle,
      image: item.image,
      button_text: item.button_text || 'Learn More',
      button_link: item.button_link || '/about'
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hero slide?')) return;
    try {
      await axios.delete(`${API_URL}/api/hero-images/${id}`, { headers: getAuthHeader() });
      toast({ title: 'Success', description: 'Hero slide deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete hero slide', variant: 'destructive' });
    }
  };

  const moveItem = async (index, direction) => {
    const newItems = [...items];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= items.length) return;
    
    // Swap items
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    // Update order values
    const orderData = newItems.map((item, i) => ({ id: item.id, order: i }));
    
    setItems(newItems);
    
    try {
      await axios.put(`${API_URL}/api/hero-images/reorder/batch`, orderData, { headers: getAuthHeader() });
      toast({ title: 'Success', description: 'Slide order updated' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reorder slides', variant: 'destructive' });
      fetchData(); // Revert on error
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      image: '',
      button_text: 'Learn More',
      button_link: '/about'
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Preview Modal */}
        {showPreview && items.length > 0 && (
          <PreviewCarousel slides={items} onClose={() => setShowPreview(false)} />
        )}

        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Hero Slides</h1>
            <p className="text-gray-500 mt-1">Manage homepage hero carousel images (max 5 slides)</p>
          </div>
          <div className="flex items-center gap-3">
            {items.length > 0 && (
              <button
                onClick={() => setShowPreview(true)}
                data-testid="preview-hero-btn"
                className="px-5 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg flex items-center space-x-2 transition-colors shadow-lg"
              >
                <Eye className="w-5 h-5" />
                <span>Preview</span>
              </button>
            )}
            {items.length < 5 && (
              <button
                onClick={() => setShowForm(true)}
                data-testid="add-hero-btn"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center space-x-2 transition-colors shadow-lg"
              >
                <Plus className="w-5 h-5" />
                <span>Add Hero Slide</span>
              </button>
            )}
          </div>
        </div>

        {/* Status Bar */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Image className="w-6 h-6 text-blue-600" />
              <span className="font-medium text-blue-900">
                {items.length}/5 slides configured
              </span>
            </div>
            <div className="text-sm text-blue-700">
              These slides appear on the homepage hero section
            </div>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingId ? 'Edit Hero Slide' : 'Add New Hero Slide'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <ImageUpload
                    value={formData.image}
                    onChange={(url) => setFormData({ ...formData, image: url })}
                    label="Hero Image *"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter slide title"
                    required
                    data-testid="hero-title-input"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle *</label>
                  <textarea
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Enter slide subtitle or description"
                    rows="3"
                    required
                    data-testid="hero-subtitle-input"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                  <input
                    type="text"
                    value={formData.button_text}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                    placeholder="Learn More"
                    data-testid="hero-button-text-input"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Button Link</label>
                  <input
                    type="text"
                    value={formData.button_link}
                    onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                    placeholder="/about"
                    data-testid="hero-button-link-input"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  data-testid="hero-submit-btn"
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Save className="w-5 h-5" />
                  <span>{editingId ? 'Update Slide' : 'Create Slide'}</span>
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Hero Slides List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading hero slides...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No hero slides yet</p>
              <p className="text-gray-400 text-sm mt-1">Create your first hero slide to display on the homepage</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {items.map((item, index) => (
                <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors" data-testid={`hero-item-${index}`}>
                  <div className="flex items-start gap-6">
                    {/* Reorder Controls */}
                    <div className="flex flex-col items-center gap-1 pt-2">
                      <button
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                        className={`p-1.5 rounded-lg transition-colors ${
                          index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                        }`}
                        title="Move up"
                      >
                        <MoveUp className="w-5 h-5" />
                      </button>
                      <div className="text-sm font-bold text-gray-400">{index + 1}</div>
                      <button
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === items.length - 1}
                        className={`p-1.5 rounded-lg transition-colors ${
                          index === items.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                        }`}
                        title="Move down"
                      >
                        <MoveDown className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Image Preview */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-48 h-28 object-cover rounded-lg shadow-md"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.subtitle}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                          {item.button_text || 'Learn More'}
                        </span>
                        <span className="text-gray-400">
                          → {item.button_link || '/about'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-start space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                        title="Edit"
                        data-testid={`edit-hero-${index}`}
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                        title="Delete"
                        data-testid={`delete-hero-${index}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-3">Tips for Hero Slides</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>Use high-quality images with a 16:9 aspect ratio for best results</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>Keep titles short and impactful (5-8 words recommended)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>Subtitles should provide context but remain concise</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>Use the up/down arrows to reorder slides - order determines display sequence</span>
            </li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminHero;
